import type { InjectionKey } from "vue";
import { computed, reactive, ref } from "vue";
import jetStreamApplication from "@org-avp/avp-3dws-jetstream";
import type { IController } from "@org-avp/avp-3dws-jetstream/dist/src/interfaces/controller";
import { CriticalError, DeckError, NonCriticalError } from "@/utils/errors";
import type { AppStore } from "./app";
import type { Logger } from "@/stores/logger";
import type { ConfigStore } from "@/stores/config";
import { Brand, type DeckCustomErrorCode } from "@/types";

export type ConnectionStore = ReturnType<typeof useConnectionStore>;
export const CONNECTION: InjectionKey<ConnectionStore> = Symbol("CONNECTION");

export const useConnectionStore = (
  appStore: AppStore,
  configStore: ConfigStore,
  log: Omit<Logger, "logId"> = console
) => {
  const connected = ref(false); // indicates the connection to AVE is done, but no content is loaded yet
  const jetStreamController = ref<IController>();
  const sessionId = ref<string>();
  const isNewSession = ref(true);

  async function init(): Promise<void> {
    const instanceMatchingUrl = await getInstanceMatchingUrl();
    try {
      jetStreamController.value = await jetStreamApplication.init({
        instanceMatchingUrl,
        inactivityTimeout: configStore.config?.inactivityTimeout,
        reconnectGracePeriod: configStore.config?.reconnectGracePeriod ?? 0
      });
      setupCleanupAfterDisconnect();
    } catch (e) {
      // Errors thrown by ave/jetstream only contain an errorMessage and errorCode, thus we want to keep it fully in our rethrown error.
      throw handleAndGenerateError(
        "CRITICAL",
        "Unable to connect to JetStream instance.",
        "NO_INSTANCES_AVAILABLE",
        e as Error
      );
    }
  }

  function setupCleanupAfterDisconnect() {
    // register handler to set the correct state when stream closure is not triggered by deck
    jetStreamController.value?.onDisconnect(() => cleanUp());

    jetStreamController.value?.onMultiSessionClose(() => {
      appStore.emitEvent({
        type: "disconnected",
        detail: {
          reason: "MULTISESSION_DISCONNECTED"
        }
      });
    });

    jetStreamController.value?.onSessionTimeoutClose(() => {
      appStore.emitEvent({
        type: "disconnected",
        detail: {
          reason: "INACTIVITY_TIMEOUT"
        }
      });
    });
  }

  async function getInstanceMatchingUrl(): Promise<string> {
    const service = configStore.config?.service;
    const market = configStore.config?.market;
    const brand = configStore.config?.brand;
    const streamingStage = configStore.config?.streamingStage;

    switch (streamingStage) {
      case "prod-euc1":
        return "https://matchmaker.eu-central-1-web-streaming.avp.tech";
      case "prod-euw1":
        return "https://matchmaker.eu-west-1-web-streaming.avp.tech";
      case "prod-eun1":
        return "https://matchmaker.eu-north-1-web-streaming.avp.tech";
      case "prod-apse1":
        return "https://matchmaker.ap-southeast-1-web-streaming.avp.tech";
      case "prod-use1":
        return "https://matchmaker.us-east-1-web-streaming.avp.tech";
      case "prod-usw2":
        return "https://matchmaker.us-west-2-web-streaming.avp.tech";
      case "sandbox":
        return "https://matchmaker.sandbox-euw1.eu-west-1-web-streaming.avp.tech";
      case "prelive-euc1":
        return "https://matchmaker.prelive-euc1.eu-central-1-web-streaming.avp.tech";
      case "prelive":
      case "prelive-euw1":
        return "https://matchmaker.prelive-euw1.eu-west-1-web-streaming.avp.tech";
      case "qa":
        return "https://matchmaker.qa-euw1.eu-west-1-web-streaming.avp.tech";
      case "dev":
        return "https://matchmaker.dev-euw1.eu-west-1-web-streaming.avp.tech";
      default:
        // Check each matchmaker in each region, if the provided (service,market,brand) is supported
        // Use Promise.any to use find the first one to respond
        return await Promise.any(
          [
            "https://matchmaker.eu-central-1-web-streaming.avp.tech",
            "https://matchmaker.eu-west-1-web-streaming.avp.tech",
            "https://matchmaker.eu-north-1-web-streaming.avp.tech",
            "https://matchmaker.ap-southeast-1-web-streaming.avp.tech",
            "https://matchmaker.us-east-1-web-streaming.avp.tech",
            "https://matchmaker.us-west-2-web-streaming.avp.tech"
          ].map(async (matchmakerUrl) => {
            const instancesResponse = await fetch(`${matchmakerUrl}/api/instances`, {
              headers: { service, market, brand } as HeadersInit
            });
            const instancesJson = await instancesResponse.json();

            if (instancesJson.totalInstancesCount === undefined) {
              return Promise.reject(
                new Error(`Received unexpected response from ${matchmakerUrl}`)
              );
            }

            if (instancesJson.totalInstancesCount === 0) {
              return Promise.reject(new Error(`No instances registered on ${matchmakerUrl}`));
            }

            return Promise.resolve(matchmakerUrl);
          })
        ).catch(async (e: AggregateError) => {
          log.error(e.errors);
          throw handleAndGenerateError(
            "CRITICAL",
            "Could not identify a suitable matching server - configuration error?",
            "CONFIGURATION_ERROR",
            e
          );
        });
    }
  }

  async function disconnect(): Promise<void> {
    if (appStore.multisessionDisconnectHtmlAttribute) {
      await jetStreamController.value?.endMultiSession();
    } else {
      await jetStreamController.value?.close();
    }
    cleanUp();
  }

  function cleanUp(): void {
    connected.value = false;
    sessionId.value = undefined;
    appStore.emitCloseEvent();
    appStore.emitEvent({
      type: "disconnected",
      detail: {
        reason: "DISCONNECTED"
      }
    });
  }

  async function connect(el: HTMLElement): Promise<void> {
    const brand = configStore.config?.brand;
    const service = configStore.config?.service;
    const market = configStore.config?.market;

    if (!jetStreamController.value) {
      log.info("Cannot connect, controller not initialized.");
      return;
    }

    if (connected.value) {
      log.info("Cannot connect, already connected.");
      return;
    }

    if (!brand) {
      throw handleAndGenerateError(
        "CRITICAL",
        "Brand not provided to Deck configuration.",
        "BRAND_NOT_PROVIDED"
      );
    }

    if (!Object.values(Brand).includes(brand)) {
      throw handleAndGenerateError(
        "CRITICAL",
        `Brand '${brand}' provided to Deck configuration is not supported.`,
        "BRAND_NOT_SUPPORTED"
      );
    }

    if (!service) {
      throw handleAndGenerateError(
        "CRITICAL",
        "Service not provided to Deck configuration.",
        "SERVICE_NOT_PROVIDED"
      );
    }

    if (!market) {
      throw handleAndGenerateError(
        "CRITICAL",
        "Market neither provided in Deck configuration nor in HTML attribute.",
        "MARKET_NOT_PROVIDED"
      );
    }

    if (!el) {
      log.warn("Could not find HTML element to spawn the stream");
      return;
    }

    try {
      if (!sessionId.value) {
        // If we don't have a session ID, we need to create one with JetStream
        sessionId.value = await jetStreamController.value.getSessionToken({
          brand,
          service,
          market
        });
      } else {
        // We are connecting to an existing session that someone else has started
        log.debug("Connecting to existing session ID", sessionId.value);
        isNewSession.value = false;
      }

      await jetStreamController.value.connectWithSessionToken(el, sessionId.value, {
        displayMode: "cover",
        skipAutomaticAveSession: true
      });
    } catch (e) {
      throw handleAndGenerateError(
        "CRITICAL",
        "Unable to connect to JetStream instance.",
        "NO_INSTANCES_AVAILABLE",
        e as Error
      );
    }

    connected.value = true;
    appStore.emitEvent({
      type: "connected",
      detail: {
        sessionId: sessionId.value
      }
    });
  }

  function handleAndGenerateError(
    severity: "CRITICAL" | "NON_CRITICAL",
    errorMessage: string,
    errorCode?: DeckCustomErrorCode,
    error?: Error | AggregateError
  ): DeckError {
    if (severity === "NON_CRITICAL") {
      // only requires errorMessage, no need for original error
      return new NonCriticalError(errorMessage);
    }

    // we do not need to await this
    disconnect();
    appStore.active = false;

    return new CriticalError(errorMessage, appStore.instance, errorCode, error);
  }

  return reactive({
    sessionId: sessionId,
    isNewSession: isNewSession,
    connected: connected,
    jetStreamController: jetStreamController,
    aveController: computed(() => jetStreamController.value?.api),
    init: init,
    connect: connect,
    disconnect: disconnect,
    handleAndGenerateError: handleAndGenerateError
  });
};
